/**
 * Capture errors with consistent severity and context.
 * Use critical for unhandled/user-blocking; warning for recoverable; info sparingly.
 */

import * as Sentry from "@sentry/nextjs";
import { SENTRY_TAGS } from "./constants";
import type { SentrySeverity } from "./constants";

export type CaptureOptions = {
  level?: SentrySeverity;
  action?: string;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
};

/**
 * Capture an exception with severity and optional action/tags.
 * Critical: 100% captured. Warning: 100%. Info: prefer breadcrumbs; if captured, sample.
 */
export function captureWithSeverity(
  error: unknown,
  options: CaptureOptions = {}
): string | undefined {
  const { level = "error", action, tags = {}, extra = {} } = options;

  const sentryLevel = level === "critical" ? "fatal" : level === "warning" ? "warning" : "info";

  return Sentry.withScope((scope) => {
    scope.setLevel(sentryLevel);
    if (action) scope.setTag(SENTRY_TAGS.action, action);
    Object.entries(tags).forEach(([k, v]) => scope.setTag(k, v));
    Object.entries(extra).forEach(([k, v]) => scope.setExtra(k, v));

    if (error instanceof Error) {
      return Sentry.captureException(error);
    }
    return Sentry.captureMessage(String(error), sentryLevel);
  });
}
